import axios, { AxiosRequestConfig } from "axios";
import Cookies from "universal-cookie";
import {
  ApiHeaders,
  UserAuth,
  GetUserAuthParams,
  StreakQuestThemeData,
  StreakBrandTheme,
  QuestResponse,
  ApiResponse,
} from "./StreakTypes";
import config from "../../../../config";
import General from "../../../../general";

class StreakApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL =
      apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosConfig(headers: ApiHeaders): AxiosRequestConfig {
    return {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    };
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    entityAuthenticationToken,
  }: GetUserAuthParams): Promise<UserAuth | null> {
    try {
      // Check cookies first
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      // If we have valid cookie data and it matches the uniqueUserId
      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken,
        };
      }

      // If uniqueUserId is provided, create new user
      if (uniqueUserId) {
        const headers = this.getAxiosConfig({
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
          ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
        });

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          headers
        );

        if (!response.data?.success) {
          throw new Error("Invalid response from external login");
        }

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, {
          path: "/",
          expires,
        });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken,
        };
      }

      // If no uniqueUserId but we have userId and token, use those
      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  async fetchCampaignData(
    entityId: string,
    questId: string,
    headers: ApiHeaders,
    variation?: string
  ): Promise<QuestResponse["data"]> {
    try {
      if (!headers.userId || !headers.token) {
        throw new Error("Missing required headers");
      }

      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const request = `${
        this.BACKEND_URL
      }api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;

      const response = await axios.get<QuestResponse>(
        request,
        this.getAxiosConfig(headers)
      );

      if (!response.data?.data) {
        throw new Error("No data received from API");
      }

      return response.data.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      throw error;
    }
  }

  async getDailyStreakCount(
    entityId: string,
    userId: string,
    counterId: string,
    headers: ApiHeaders
  ): Promise<number> {
    try {
      const request = `${this.BACKEND_URL}api/entities/${entityId}/counters/${counterId}/user?userId=${userId}`;
      const response = await axios.get<ApiResponse<{ counter: number }>>(
        request,
        this.getAxiosConfig(headers)
      );
      return response?.data?.data?.[0]?.counter || 0;
    } catch (error) {
      console.error(error);
      this.GeneralFunctions.captureSentryException(error);
      return 0;
    }
  }

  trackEvent(eventName: string, category: string) {
    this.GeneralFunctions.fireTrackingEvent(eventName, category);
  }

  clearCookies(): void {
    this.cookies.remove("externalUserId", { path: "/" });
    this.cookies.remove("questUserId", { path: "/" });
    this.cookies.remove("questUserToken", { path: "/" });
  }

  isValidUserAuth(userAuth: unknown): userAuth is UserAuth {
    return (
      !!userAuth &&
      typeof (userAuth as UserAuth).userId === "string" &&
      typeof (userAuth as UserAuth).token === "string"
    );
  }
}

export default StreakApi;
